<script setup>
import "ag-grid-community/styles/ag-grid.css"
import "ag-grid-community/styles/ag-theme-alpine.css"
import "@css/ag-grid.css"
import { AgGridVue } from "ag-grid-vue3"
import { reactive, computed, ref, onMounted } from "vue"
import { Inertia } from '@inertiajs/inertia'
import { PencilIcon, GlobeAltIcon } from "@heroicons/vue/outline"

import InvoiceListActions from '@/Components/Models/Invoice/ListActions.vue'
import DueDateListItem from "@/Components/Models/Invoice/DueDateListItem.vue"
import HideColumnsButton from '@/Shared/AGGrid/HideColumnsButton'
import ExportCSVButton from '@/Shared/AGGrid/ExportCSVButton'

import { useErrorsStore } from '@/Stores/errors'
const errors = useErrorsStore()

const props = defineProps({
    organisation: {
        type: Object,
        default: () => ({}),
    },
    invoices: {
        type: Array,
        default: () => [],
    },
    hasFetchedInvoices: {
        type: Boolean,
        default: false,
    },
	columnDefs: {
		type: Array,
		default: () => ([
			{ 
				field: "InvoiceNumber",
				minWidth: 180,
				headerName: "Inv #",
			},
			{ 
				field: "AmountPaid",
				minWidth: 120,
				headerName: "Paid",
				type: 'rightAligned',
			},
			{ 
				field: "AmountDue", 
				minWidth: 120,
				headerName: "Due",
				type: 'rightAligned',
			},
			{ 
				minWidth: 100,
				field: "DueDate",
				headerName: "Due Date",
				type: 'rightAligned',
				cellRenderer: DueDateListItem,
			},
			{
				field: "Status", 
				minWidth: 140,
				headerName: "Status",
			},
			{ 
				field: "actions", 
				minWidth: 200,
				cellRenderer: InvoiceListActions,
			}
		])
	}
})

const emit = defineEmits([
    'item-clicked',
    'invoices-loaded',
    'invoices-error',
])

function rowClicked(event){
	emit('item-clicked', event.data)
}

const rowData = ref(props.invoices)

const defaultColDef = {
	sortable: true,
	filter: true,
	flex: 1,
	dataTestid: 'column',
	resizable: true,
	show: true,
}

const localColumnDefs = reactive(props.columnDefs)
function toggleColumn(data){
	var col = localColumnDefs.find(x => x.field == data.field)
	col.hide = data.hide
}

const loadingError = ref(null)

const page = ref(1)
function fetchItems() {
    return axios.get('/api/xero-invoices?page=' + page.value)
    .then(response => {
        emit('invoices-loaded', response.data)
		if(response.data.length){
			page.value ++
			fetchItems()
		}
    })
    .catch(error => {
		loadingError.value = {
			type: 'error',
			title: 'Error fetching invoices',
            message: error.response?.data?.message,
        }
        errors.push(loadingError.value)
		emit('invoices-error', loadingError.value)
    })
}

onMounted(() => {
    if(!props.hasFetchedInvoices){
        fetchItems()
    }
})

function clickedGrid(event){
	savePageNumber()
}

function savePageNumber(event){
	if(gridApi.value) {
		localStorage.setItem('invoice-list-cur-page', gridApi.value.paginationGetCurrentPage())
	}
}

const gridApi = ref(null)
function onGridReady(params) {
	gridApi.value = params.api
}

function onFirstDataRendered(params) {
	if(localStorage.getItem('invoice-list-cur-page')){
		gridApi.value.paginationGoToPage(parseInt(localStorage.getItem('invoice-list-cur-page')))
	}
}

// we want to be able to disable pagination if the user checks the "show all" box
const enablePagination = ref(true)
const perPage = computed(() => {
	return enablePagination.value ? 10 : props.items.length +1
})
function togglePagination(){
	enablePagination.value = !enablePagination.value
	gridApi.value.paginationSetPageSize(perPage.value)
}
const gridClasses = computed(() => {
	return enablePagination.value ? '' : 'hide-pagination'
})

</script>

<template>
	<div data-testid="invoice-list">
		<BlockLoader class="h-64 w-full" v-if="!hasFetchedInvoices && !loadingError" />
		<div v-else class="flex flex-col h-full grow">
			<ag-grid-vue
				class="ag-theme-alpine"
				:class="gridClasses"
				domLayout='autoHeight'
				:rowHeight="48"
				rowClass="invoice-list-row"
				:columnDefs="localColumnDefs"
				:rowData="invoices"
				:defaultColDef="defaultColDef"
				rowSelection="none"
				animateRows="true"
				@row-clicked="rowClicked"
				:pagination="enablePagination"
				:paginationPageSize="perPage"
				@grid-ready="onGridReady"
				:context="{ organisation: organisation }"
				@click="clickedGrid"
				@first-data-rendered="onFirstDataRendered"
			>
			</ag-grid-vue>
		</div>
	</div>
		
	<div class="w-full flex justify-end pr-4 pt-1">
		<HideColumnsButton
			:columnDefs="localColumnDefs"
			@toggle-column="toggleColumn"
		/>

		<ExportCSVButton
			:grid-api="gridApi"
		/>

		<Button
			class="mb-4 ml-1 text-xs py-1 px-2 self-end"
			color="light-grey"
			@click="togglePagination"
		>
			Show {{enablePagination ? 'All' : 'Pages'}}
		</Button>
	</div>
</template>


